import {
  type AssistantConsultantDeadlinesConfirmResponse,
  type AssistantSendRequest,
  type AssistantSendResponse,
  type AssistantThread,
  isConsultantDeadlinesAssistantPage,
} from '@prospero/shared/src/assistant/assistant-contract';
import { inject } from 'inversify';

import { Injectable } from '../../decorators/injectable';
import { getHttpErrorBody, getHttpErrorStatus, isResponseError } from '../../utils/error.utils';
import { HttpService, type IHttpService } from '../http/http.service';
import { AssistantVoiceError } from './assistant-voice.error';

export interface AssistantUploadResponse {
  attachmentId: string;
  filename: string;
}

export interface AssistantConfirmResponse {
  /** Absent for a REGISTER_TEMPERATURE_READING confirm, which creates a record, not a document. */
  documentId?: string;
  /** ARCHIVE_DOCUMENT_DRAFT only; NC confirms return ncClosed/linkCompleted instead. */
  alreadyArchived?: boolean;
  /** REGISTER_TEMPERATURE_READING only: the reading written to the register. */
  recordId?: string;
  /** REGISTER_TEMPERATURE_READING only: verdict computed by the HACCP validator. */
  isCompliant?: boolean;
}

function isAssistantVoiceErrorBody(
  body: unknown,
  code: AssistantVoiceError['code'],
): body is { code: AssistantVoiceError['code'] } {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return false;
  }
  const entries = Object.entries(body);
  return entries.length === 1 && entries[0]?.[0] === 'code' && entries[0][1] === code;
}

export interface IAssistantService {
  getThread(pageUrl?: string): Promise<AssistantThread>;
  sendMessage(input: AssistantSendRequest): Promise<AssistantSendResponse>;
  sendVoiceMessage(blob: Blob, pageUrl: string): Promise<AssistantSendResponse>;
  resetConversation(pageUrl?: string): Promise<AssistantThread>;
  uploadAttachment(file: File, pageUrl?: string): Promise<AssistantUploadResponse>;
  confirmAction(actionId: string): Promise<AssistantConfirmResponse>;
  confirmConsultantDeadlinesAction(
    actionId: string,
  ): Promise<AssistantConsultantDeadlinesConfirmResponse>;
}

@Injectable()
export class AssistantService implements IAssistantService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  getThread(pageUrl?: string): Promise<AssistantThread> {
    // pageUrl lets the backend route a studio-page read to the authoring
    // thread on the user's CONSULTANT workspace (same tenant rule as the
    // send); on every other route the backend ignores it.
    return this.httpService.get<AssistantThread>(
      pageUrl ? `assistant/messages?pageUrl=${encodeURIComponent(pageUrl)}` : 'assistant/messages',
    );
  }

  sendMessage(input: AssistantSendRequest): Promise<AssistantSendResponse> {
    return this.httpService.post<AssistantSendResponse>('assistant/messages', input);
  }

  async sendVoiceMessage(blob: Blob, pageUrl: string): Promise<AssistantSendResponse> {
    const formData = new FormData();
    const filename = blob.type.includes('mp4') ? 'voice.m4a' : 'voice.webm';
    formData.append('file', blob, filename);
    formData.append('pageUrl', pageUrl);
    try {
      // Deadline page: the query lets the backend resolve the consultant
      // workspace before the multipart body is parsed.
      return await this.httpService.postForm<AssistantSendResponse>(
        isConsultantDeadlinesAssistantPage(pageUrl)
          ? `assistant/voice-messages?pageUrl=${encodeURIComponent(pageUrl)}`
          : 'assistant/voice-messages',
        formData,
      );
    } catch (error) {
      if (isResponseError(error)) {
        const status = getHttpErrorStatus(error);
        const body = getHttpErrorBody(error);
        if (status === 422 && isAssistantVoiceErrorBody(body, 'voice_empty')) {
          throw new AssistantVoiceError('voice_empty');
        }
        if (status === 503 && isAssistantVoiceErrorBody(body, 'voice_transcription_failed')) {
          throw new AssistantVoiceError('voice_transcription_failed');
        }
      }
      throw error;
    }
  }

  resetConversation(pageUrl?: string): Promise<AssistantThread> {
    // Query param (the DELETE route has no body): on a studio pageUrl the
    // reset targets the authoring thread on the CONSULTANT workspace.
    return this.httpService.delete<AssistantThread>(
      pageUrl ? `assistant/messages?pageUrl=${encodeURIComponent(pageUrl)}` : 'assistant/messages',
    );
  }

  uploadAttachment(file: File, pageUrl?: string): Promise<AssistantUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    // Deadline page: the backend stages the file on the consultant workspace.
    return this.httpService.postForm<AssistantUploadResponse>(
      pageUrl
        ? `assistant/attachments?pageUrl=${encodeURIComponent(pageUrl)}`
        : 'assistant/attachments',
      formData,
    );
  }

  confirmConsultantDeadlinesAction(
    actionId: string,
  ): Promise<AssistantConsultantDeadlinesConfirmResponse> {
    return this.httpService.post<AssistantConsultantDeadlinesConfirmResponse>(
      `assistant/consultant-deadline-actions/${encodeURIComponent(actionId)}/confirm`,
    );
  }

  confirmAction(actionId: string): Promise<AssistantConfirmResponse> {
    return this.httpService.post<AssistantConfirmResponse>(`assistant/actions/${actionId}/confirm`);
  }
}
